'use client';

// Messages sent while Poneva works, before it reads them (CONTRACT v1). They
// wait dimmed at the end of the chat, where they were sent, in the pending
// voice turn's pattern (ui/voice/pending-bubble.tsx): never moved, they turn
// solid in the answer once read, or become the next message. One status line
// says what happens next and carries the one action that changes it.
import { useAuiState } from '@assistant-ui/react';
import { PencilIcon, XIcon } from 'lucide-react';
import { useSyncExternalStore, type FC } from 'react';
import { Button } from '@/components/ui/button';
import { touchControlVariants } from '@/components/ui/touch-control';
import { useNamespaceCopy } from '@/i18n/ns';
import steerCopy, { type SteerCopyKey } from '@/i18n/ns/steer';
import { cn } from '@/lib/utils';
import { useSteer } from '@/lib/steer/use-steer';
import { steerStore, type SteerEntry, type SteerHold } from '@/lib/steer/store';
import { routeFor } from '@/workspace/navigation';
import { WaitingAttachments } from './waiting-attachments';

type Status =
  | { kind: 'live'; label: SteerCopyKey; sendNow: boolean }
  | { kind: 'held'; hold: SteerHold; label: SteerCopyKey }
  | { kind: 'sending'; label: SteerCopyKey };

const HELD_LABEL: Record<SteerHold, SteerCopyKey> = {
  stopped: 'notSent',
  failed: 'heldFailed',
  question: 'heldQuestion',
  approval: 'heldApproval',
  credits: 'heldCredits',
};

export function waitingStatus(
  entries: readonly SteerEntry[],
  running: boolean,
  writing: boolean,
): Status {
  const held = entries.find((entry) => entry.state === 'held');
  if (held) {
    const hold = held.hold ?? 'stopped';
    return { kind: 'held', hold, label: HELD_LABEL[hold] };
  }
  if (!running) return { kind: 'sending', label: 'sending' };
  const many = entries.length > 1;
  if (writing || entries.some((entry) => entry.attachments?.length))
    return { kind: 'live', label: many ? 'goesNextMany' : 'goesNext', sendNow: true };
  return { kind: 'live', label: many ? 'readNextStepMany' : 'readNextStep', sendNow: true };
}

type LastPart = { type: string; text?: string };

export const WaitingMessages: FC = () => {
  const steer = useSteer();
  const copy = useNamespaceCopy(steerCopy);
  const threadId = steer?.threadId ?? null;
  const entries = useSyncExternalStore(
    steerStore.subscribe,
    () => steerStore.snapshot(threadId),
    () => steerStore.snapshot(null),
  );
  const running = useAuiState((state) => state.thread.isRunning);
  // Poneva is writing its answer: the newest part is text. A later tool step
  // would read the message after all, so this is only what the person sees now.
  const writing = useAuiState((state) => {
    if (!state.thread.isRunning) return false;
    const last = state.thread.messages.at(-1) as
      | { role: string; content: readonly LastPart[] }
      | undefined;
    const part = last?.role === 'assistant' ? last.content.at(-1) : undefined;
    return part?.type === 'text' && Boolean(part.text?.trim());
  });
  if (!steer || !entries.length) return null;
  const { actions } = steer;
  const status = waitingStatus(entries, running, writing);
  const control = touchControlVariants({ density: 'icon' });

  return (
    <div
      data-slot="steer-waiting"
      role="group"
      aria-label={copy.waitingLabel}
      className="flex w-full flex-col items-end gap-2 px-2"
    >
      {entries.map((entry, index) => (
        <div
          key={entry.id}
          data-slot="steer-waiting-message"
          data-state={entry.state}
          className="group/steer fade-in slide-in-from-bottom-1 animate-in grid max-w-[88%] grid-cols-[auto_minmax(0,1fr)] items-center justify-end gap-x-1 gap-y-2 duration-150 motion-reduce:animate-none"
        >
          {!!entry.attachments?.length && <WaitingAttachments attachments={entry.attachments} />}
          <div
            className={cn(
              'col-start-1 flex items-center opacity-0 transition-opacity duration-150 group-hover/steer:opacity-100 group-focus-within/steer:opacity-100 [@media(hover:none)]:opacity-100',
              entry.attachments?.length && entry.text ? 'row-start-2' : 'row-start-1',
              index === entries.length - 1 && 'opacity-100',
            )}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={cn(control, 'text-muted-foreground rounded-lg')}
              aria-label={copy.edit}
              title={copy.edit}
              onClick={() => void actions.edit(entry.id)}
            >
              <PencilIcon className="size-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className={cn(control, 'text-muted-foreground rounded-lg')}
              aria-label={copy.remove}
              title={copy.remove}
              onClick={() => void actions.remove(entry.id)}
            >
              <XIcon className="size-4" aria-hidden="true" />
            </Button>
          </div>
          {entry.text && (
            <div
              dir="auto"
              data-slot="steer-waiting-text"
              className={cn(
                'bg-muted text-foreground col-start-2 min-w-0 rounded-2xl rounded-ee-md px-4 py-2.5 break-words whitespace-pre-wrap opacity-60',
                entry.attachments?.length ? 'row-start-2' : 'row-start-1',
              )}
            >
              {entry.text}
            </div>
          )}
        </div>
      ))}
      <div
        data-slot="steer-waiting-status"
        className="-mt-0.5 flex flex-wrap items-center justify-end gap-1"
      >
        <span
          role="status"
          className="text-muted-foreground flex items-center gap-1.5 px-1 text-xs"
        >
          <span
            aria-hidden="true"
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              status.kind === 'held'
                ? 'bg-muted-foreground/45'
                : 'bg-brand animate-pulse motion-reduce:animate-none',
            )}
          />
          {copy[status.label]}
        </span>
        {status.kind === 'live' && status.sendNow && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              touchControlVariants({ density: 'text' }),
              'h-7 rounded-full px-3 text-xs',
            )}
            title={copy.sendNowHint}
            aria-keyshortcuts="Control+Enter Meta+Enter"
            onClick={() => void actions.sendNow()}
          >
            {copy.sendNow}
          </Button>
        )}
        {status.kind === 'held' && (status.hold === 'stopped' || status.hold === 'failed') && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                touchControlVariants({ density: 'text' }),
                'h-7 rounded-full px-3 text-xs',
              )}
              onClick={() => void actions.clearAll()}
            >
              {copy.clear}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className={cn(
                touchControlVariants({ density: 'text' }),
                'h-7 rounded-full px-3 text-xs',
              )}
              onClick={() => actions.sendHeld()}
            >
              {copy.send}
            </Button>
          </>
        )}
        {status.kind === 'held' && (status.hold === 'question' || status.hold === 'approval') && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              touchControlVariants({ density: 'text' }),
              'h-7 rounded-full px-3 text-xs',
            )}
            onClick={() => actions.sendHeld()}
          >
            {copy.sendAnyway}
          </Button>
        )}
        {status.kind === 'held' && status.hold === 'credits' && (
          <a
            href={routeFor('billing')}
            className={cn(
              touchControlVariants({ density: 'text' }),
              'border-border bg-background inline-flex h-7 items-center rounded-full border px-3 text-xs font-medium',
            )}
          >
            {copy.seePlans}
          </a>
        )}
      </div>
    </div>
  );
};
